"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslations } from "next-intl";
import { useApi } from "@/contexts/ApiContext/ApiContext";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";
import { useSessionId } from "@/contexts/SessionContext/SessionContext";
import { getAssetUrl } from "@/utils/assetUrl";
import { trackCtaClick } from "@/utils/commonEvents";
import Image from "next/image";

const RESMED_URL = "https://www.resmed.com";
import { AssessmentResult } from "@/api/types";
import {
  QUESTIONNAIRE_ID,
  SURVEY_VERSION,
  SURVEY_VARIANT,
  API_CLIENT,
} from "@/constants/surveyMetadata";
import ProcessingState from "./ProcessingState";
import { useVariant, PAGE_VARIANT } from "@/hooks/useVariant";
import { pushPageContext, formatExperimentVariants } from "@/utils/analytics";
import { getMarketingConsent } from "@/utils/marketingConsent";
import ErrorScreen from "@/components/ErrorScreen/ErrorScreen";

const STATE_1_DURATION = 2500;
const STATE_3_DURATION = 2000;
const FINAL_CHECKMARK_DURATION = 500;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 60000;

export default function PollingScreen() {
  const t = useTranslations("polling-screen");
  const tCommon = useTranslations("common");
  const api = useApi();
  const { completeAssessment, answers, experimentAssignments } =
    useAssessment();
  const { sessionId } = useSessionId();
  const variant = useVariant();
  const pageVariant = PAGE_VARIANT[variant];

  // Push sa-loading page context on mount
  useEffect(() => {
    pushPageContext(
      "sa-loading",
      pageVariant,
      formatExperimentVariants(experimentAssignments),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires only on mount; variant is stable at this point
  }, []);

  const [currentState, setCurrentState] = useState(1);
  const [completedStates, setCompletedStates] = useState<number[]>([]);
  const [result, setResult] = useState<AssessmentResult | null>(null);
  const [apiCallComplete, setApiCallComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const submittedRef = useRef(false);
  const inferenceIdRef = useRef<string | null>(null);

  // Submit the completed response, then poll for results
  useEffect(() => {
    if (!sessionId) {
      setHasError(true);
      return;
    }
    if (submittedRef.current) return;
    submittedRef.current = true;

    const controller = new AbortController();
    const MAX_ERROR_RETRIES = 3;
    const maximumWaitTimeRef: {
      current: ReturnType<typeof setTimeout> | null;
    } = { current: null };

    const clearAllTimers = () => {
      if (maximumWaitTimeRef.current) {
        clearTimeout(maximumWaitTimeRef.current);
        maximumWaitTimeRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };

    const showErrorScreen = () => {
      controller.abort(); // Signal the polling loop to stop processing
      clearAllTimers(); // Stop polling and deadline timers
      setHasError(true); // Render the error screen
    };

    const submitAndPoll = async () => {
      try {
        const inferenceId = await api.postResponse({
          id: sessionId,
          surveyId: QUESTIONNAIRE_ID,
          surveyVersion: SURVEY_VERSION,
          surveyVariant: SURVEY_VARIANT,
          apiClient: API_CLIENT,
          answers,
          ...(experimentAssignments && { experimentAssignments }),
          marketingConsent: getMarketingConsent(),
        });
        if (!inferenceId) {
          console.error(
            "Failed to submit completed response: missing inference id",
          );
          showErrorScreen();
          return;
        }
        inferenceIdRef.current = inferenceId;
      } catch (err) {
        console.error("Failed to submit completed response:", err);
        showErrorScreen();
        return;
      }

      poll();
    };

    const poll = async (errorRetries = 0) => {
      if (controller.signal.aborted) return;

      const outcome = await api.checkForResult(
        sessionId,
        inferenceIdRef.current!,
      );

      if (controller.signal.aborted) return;

      if (outcome.status === "ready") {
        clearAllTimers();
        setResult(outcome.result);
        setApiCallComplete(true);
      } else if (outcome.status === "pending") {
        pollTimeoutRef.current = setTimeout(poll, POLL_INTERVAL_MS);
        // If the backend returned an error status, retry 3 more times then send the user to an error screen
      } else {
        if (errorRetries < MAX_ERROR_RETRIES) {
          console.warn(
            `Polling error, retry attempt (${errorRetries + 1} of ${MAX_ERROR_RETRIES})`,
          );
          pollTimeoutRef.current = setTimeout(
            () => poll(errorRetries + 1),
            POLL_INTERVAL_MS,
          );
        } else {
          console.error("Polling failed after max retries");
          showErrorScreen();
        }
      }
    };

    maximumWaitTimeRef.current = setTimeout(() => {
      showErrorScreen();
    }, POLL_TIMEOUT_MS);

    submitAndPoll();

    // Avoid attempting to update state on a component that unmounted while a call was in flight
    return () => {
      controller.abort();
      clearAllTimers();
    };
    // Answers are already complete when PollingScreen mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinue = useCallback(() => {
    if (!result || !inferenceIdRef.current) return;
    completeAssessment(result, inferenceIdRef.current);
  }, [result, completeAssessment]);

  // State machine — advances through the polling steps then navigates
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (currentState === 1) {
      timer = setTimeout(() => {
        setCompletedStates([1]);
        setCurrentState(2);
      }, STATE_1_DURATION);
    } else if (currentState === 2) {
      // Advance only once the API call is complete
      if (apiCallComplete) {
        setCompletedStates([1, 2]);
        setCurrentState(3);
      }
    } else if (currentState === 3) {
      timer = setTimeout(() => {
        setCompletedStates([1, 2, 3]);
        setCurrentState(4);
      }, STATE_3_DURATION);
    } else if (currentState === 4) {
      timer = setTimeout(() => {
        handleContinue();
      }, FINAL_CHECKMARK_DURATION);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [currentState, handleContinue, apiCallComplete]);

  if (hasError) {
    return <ErrorScreen />;
  }

  return (
    <div className="polling-screen" data-cy="polling-screen">
      {/* Fallback background */}
      <div
        className="polling-screen-fallback-bg"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          backgroundImage: `url(${getAssetUrl("/images/polling_screen_background_poster.webp")})`,
        }}
      />

      {/* Starry night background video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
        poster={getAssetUrl("/images/polling_screen_background_poster.webp")}
        tabIndex={-1}
        className="polling-screen-video"
      >
        <source
          src={getAssetUrl("/images/polling_screen_background_video.mp4")}
          type="video/mp4"
        />
      </video>

      {/* Logo */}
      <header className="polling-screen-header">
        <a
          href={RESMED_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="polling-screen-logo-link"
          aria-label={tCommon("resmed-logo-alt")}
          onClick={() =>
            trackCtaClick({
              componentName: "PollingScreen",
              clickedArea: "logo",
              clickedText: tCommon("resmed-logo-alt"),
              clickedUrl: RESMED_URL,
            })
          }
        >
          <Image
            src={getAssetUrl("/images/resmed_logo_white.svg")}
            alt={tCommon("resmed-logo-alt")}
            width={166}
            height={44}
            className="polling-screen-logo"
            unoptimized
          />
        </a>
      </header>

      {/* Centered processing states */}
      <div className="polling-screen-content">
        <div className="polling-screen-states">
          <ProcessingState
            text={t("reviewing-your-answers")}
            isActive={currentState === 1}
            isComplete={completedStates.includes(1)}
            dataCy="processing-state-1"
            t={t}
          />
          <ProcessingState
            text={t("assessing-sleep-data")}
            isActive={currentState === 2}
            isComplete={completedStates.includes(2)}
            dataCy="processing-state-2"
            t={t}
          />
          <ProcessingState
            text={t("preparing-your-results")}
            isActive={currentState === 3}
            isComplete={completedStates.includes(3)}
            dataCy="processing-state-3"
            t={t}
          />
        </div>
      </div>
    </div>
  );
}
